import React, { forwardRef } from "react";

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "filled" | "ghost" | "outline";
  size?: "xs" | "sm" | "md" | "lg";
  loading?: boolean;
  rounded?: boolean;
}

/**
 * Arc-style IconButton component
 * 
 * Features:
 * - Multiple variants: default, filled, ghost, outline
 * - Multiple sizes: xs, sm, md, lg
 * - Loading state
 * - Optional fully rounded style
 * - Smooth hover and active transitions
 */
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      variant = "ghost",
      size = "md",
      loading = false,
      disabled = false,
      rounded = false,
      children,
      className = "",
      type = "button",
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    // Base styles
    const baseStyles = `
      inline-flex items-center justify-center
      transition-all duration-arc-normal ease-arc
      focus:outline-none
      focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-accent
      disabled:cursor-not-allowed disabled:opacity-50
      select-none
    `;

    // Size styles (square dimensions)
    const sizeStyles = {
      xs: "w-6 h-6 text-xs",
      sm: "w-8 h-8 text-sm",
      md: "w-10 h-10 text-base",
      lg: "w-12 h-12 text-lg",
    };

    // Border radius
    const radiusStyles = rounded ? "rounded-full" : "rounded-arc-md";

    // Variant styles
    const variantStyles = {
      default: `
        bg-surface-secondary text-content-primary
        hover:bg-surface-tertiary
        active:scale-95
      `,
      filled: `
        bg-accent text-white
        hover:bg-accent-light
        active:scale-95
      `,
      ghost: `
        bg-transparent text-content-secondary
        hover:bg-surface-secondary hover:text-content-primary
        active:bg-surface-tertiary active:scale-95
      `,
      outline: `
        bg-transparent text-content-primary
        border border-arc-border
        hover:bg-surface-secondary hover:border-arc-border-strong
        active:scale-95
      `,
    };

    // Loading spinner
    const LoadingSpinner = () => (
      <svg
        className="animate-arc-spin w-4 h-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="3"
        />
        <path
          className="opacity-90"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={`
          ${baseStyles}
          ${sizeStyles[size]}
          ${radiusStyles}
          ${variantStyles[variant]}
          ${className}
        `}
        {...props}
      >
        {loading ? <LoadingSpinner /> : children}
      </button>
    );
  }
);

IconButton.displayName = "IconButton";

export default IconButton;
